import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Check, Mail } from 'lucide-react';
import { faqs } from '../data/mock';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [selectedAges, setSelectedAges] = useState([]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const futureAges = [13, 14, 15, 16, 17, 18];

  // Add teen ages FAQ to the list
  const allFaqs = [
    ...faqs,
    {
      question: "Do you have curriculum for teens (ages 13-18)?",
      answer: "We're currently developing curriculum for teen ages 13-18. The teen years present unique emotional challenges including identity formation, peer pressure, romantic relationships, and preparing for independence. Our teen curriculum will address these specific developmental needs with age-appropriate scripts and tools. Sign up below to be notified when teen ages become available."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const toggleAge = (age) => {
    setSelectedAges(prev => 
      prev.includes(age) ? prev.filter(a => a !== age) : [...prev, age]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedAges.length === 0) return;
    
    setSubmitting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/age-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name: name || null,
          requested_ages: selectedAges,
          message: message || null
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setShowRequestForm(false);
          setSelectedAges([]);
          setEmail('');
          setName('');
          setMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting request:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section" style={{ background: 'var(--gradient-hero-subtle)', paddingTop: '4rem' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="heading-hero mb-4">Frequently Asked Questions</h1>
            <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
              Everything you need to know about the Emotional Intelligence Academy curriculum.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="space-y-4">
              {allFaqs.map((faq, index) => (
                <div 
                  key={index}
                  className="card cursor-pointer hover-lift"
                  onClick={() => toggleFAQ(index)}
                >
                  <div className="flex justify-between items-start gap-4">
                    <h3 className="heading-3 flex-1">{faq.question}</h3>
                    <button className="flex-shrink-0 p-1">
                      {openIndex === index ? (
                        <ChevronUp size={24} style={{ color: 'var(--brand-orange)' }} />
                      ) : (
                        <ChevronDown size={24} style={{ color: 'var(--text-muted)' }} />
                      )}
                    </button>
                  </div>
                  
                  {openIndex === index && (
                    <div className="mt-4 pt-4 border-t border-border-light">
                      <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Request Teen Ages Section */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-2xl mx-auto">
            <div className="card text-center">
              <h2 className="heading-2 mb-4">Looking for Ages 13-18?</h2>
              <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
                We're working on expanding our curriculum to cover teen years. 
                Let us know which ages you're interested in and we'll notify you when they're available.
              </p>
              
              {!showRequestForm ? (
                <button 
                  onClick={() => setShowRequestForm(true)}
                  className="btn-primary"
                >
                  Request Teen Ages (13-18)
                </button>
              ) : submitted ? (
                <div className="py-8">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-100">
                    <Check size={32} className="text-green-600" />
                  </div>
                  <h3 className="heading-3 mb-2">Thank You!</h3>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                    We've received your request and will notify you when these ages become available.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="text-left">
                  <div className="mb-6">
                    <label className="body-small font-medium mb-3 block text-center">Select Ages You're Interested In *</label>
                    <div className="flex flex-wrap gap-3 justify-center">
                      {futureAges.map(age => (
                        <button
                          key={age}
                          type="button"
                          onClick={() => toggleAge(age)}
                          className={`w-14 h-14 rounded-full font-bold text-lg transition-all ${
                            selectedAges.includes(age)
                              ? 'text-white shadow-md'
                              : 'bg-white border-2 border-gray-200 hover:border-gray-300'
                          }`}
                          style={selectedAges.includes(age) ? { background: 'linear-gradient(135deg, #5B7FFF 0%, #A78BFA 100%)' } : {}}
                        >
                          {age}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="body-small font-medium mb-2 block">Email *</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-border-input focus:border-border-input-focus outline-none"
                        style={{ background: 'rgba(255, 255, 255, 0.7)' }}
                      />
                    </div>
                    <div>
                      <label className="body-small font-medium mb-2 block">Name (optional)</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        className="w-full px-4 py-3 rounded-lg border border-border-input focus:border-border-input-focus outline-none"
                        style={{ background: 'rgba(255, 255, 255, 0.7)' }}
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="body-small font-medium mb-2 block">Message (optional)</label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Any specific needs or questions about the teen curriculum?"
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg border border-border-input focus:border-border-input-focus outline-none resize-none"
                      style={{ background: 'rgba(255, 255, 255, 0.7)' }}
                    />
                  </div>

                  <div className="flex gap-3 justify-center">
                    <button
                      type="submit"
                      disabled={submitting || selectedAges.length === 0}
                      className="btn-primary flex items-center gap-2"
                      style={{ opacity: (submitting || selectedAges.length === 0) ? 0.6 : 1 }}
                    >
                      <Mail size={18} />
                      {submitting ? 'Submitting...' : 'Submit Request'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowRequestForm(false)}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Still Have Questions Section */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="max-w-2xl mx-auto card text-center">
            <h2 className="heading-2 mb-4">Still Have Questions?</h2>
            <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
              Browse our blog for more detailed information about emotional intelligence 
              development and parenting strategies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/start-here" className="btn-primary">
                Browse Books by Age
              </Link>
              <Link to="/blog" className="btn-secondary">
                Read Our Blog
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Guide Section */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-1 text-center mb-8">Quick Start Guide</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="card text-center" style={{ background: 'var(--accent-purple-200)' }}>
                <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(255, 255, 255, 0.6)' }}>
                  <span className="text-2xl font-bold">1</span>
                </div>
                <h3 className="heading-3 mb-2">Choose Child's Age</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Start with your child's current age. Each book is built for that developmental stage.
                </p>
                <Link to="/start-here" className="btn-primary text-sm mt-4 inline-block">
                  Find Your Child's Book
                </Link>
              </div>

              <div className="card text-center" style={{ background: 'var(--accent-blue-200)' }}>
                <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(255, 255, 255, 0.6)' }}>
                  <span className="text-2xl font-bold">2</span>
                </div>
                <h3 className="heading-3 mb-2">Get the Book</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Purchase on Amazon in paperback or Kindle format. Start whenever you're ready.
                </p>
                <Link to="/start-here" className="btn-primary text-sm mt-4 inline-block">
                  View All Books
                </Link>
              </div>

              <div className="card text-center" style={{ background: 'var(--accent-orange-200)' }}>
                <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(255, 255, 255, 0.6)' }}>
                  <span className="text-2xl font-bold">3</span>
                </div>
                <h3 className="heading-3 mb-2">Start Practicing</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Follow the weekly sequence. Practice 5-10 mins daily or 1 longer session per week, adapting to your schedule.
                </p>
                <Link to="/series-overview" className="btn-primary text-sm mt-4 inline-block">
                  How It Works
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
